/**
 * The pe3d endpoints (src/server/routes/pe3d.js), in the `{ ok, status, data | error }`
 * shape jobsApi.postJson answers with. Export and the demo link are background
 * jobs, followed through pe3d's own status endpoint, where the caller's grant
 * on the base is checked.
 */

import { getServerUri } from "./uriUtility";
import { followJob, postJson, runJob } from "./jobsApi";

export const PE3D_JOB_STATUS_PATH = "pe3d/jobStatus";

// ── Catalog ─────────────────────────────────────────────────────────────────
/** The games a new variant can be started from on a base. */
export const listOpenGames = (baseID) => postJson("pe3d/catalog/games", { baseID });
// Which games a base offers; for holders of "Grant pe3d games to bases".
export const listBaseGames = (baseID) => postJson("pe3d/base/games", { baseID });
export const setBaseGames = (baseID, games) => postJson("pe3d/base/setGames", { baseID, games });
export const listAdminGames = () => postJson("pe3d/catalog/adminGames", {});
export const listRegistryPackages = () => postJson("pe3d/catalog/registryPackages", {});
export const listRegistryVersions = (packageName) => postJson("pe3d/catalog/registryVersions", { package: packageName });
export const importRelease = (packageName, version) => postJson("pe3d/catalog/importRelease", { package: packageName, version });

// ── Releases ────────────────────────────────────────────────────────────────
export const fetchReleaseBundle = (game, release) => postJson("pe3d/release/bundle", { game, release });

/**
 * One file of a release — a template, the pack, the variant code, a game
 * variant's asset. `as` is "text" or "bytes" (an ArrayBuffer).
 */
export async function fetchReleaseFile(game, release, path, as = "text") {
  const response = await fetch(getServerUri("pe3d/release/file"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ accessToken: localStorage.getItem("session.accessToken"), game, release, path }),
  });
  if (!response.ok) throw new Error(`${path}: ${response.status} ${await response.text()}`);
  return as === "bytes" ? response.arrayBuffer() : response.text();
}

/**
 * One slot's file — a release file or a media library file — as the release
 * reads it: { file, data, parts, meta, errors }, bytes in base64.
 */
export const fetchSlotSource = (game, release, slot, ref) => postJson("pe3d/release/slotSource", { game, release, slot, ref });

// ── Variants ────────────────────────────────────────────────────────────────
export const findVariants = (baseID) => postJson("pe3d/variant/find", { baseID });
export const findVariant = (variantID) => postJson("pe3d/variant/findOne", { variantID });
export const insertVariant = (params) => postJson("pe3d/variant/insert", params);
export const updateVariant = (params) => postJson("pe3d/variant/updateOne", params);
export const duplicateVariant = (variantID, title) => postJson("pe3d/variant/duplicate", { variantID, title });
export const deleteVariant = (variantID) => postJson("pe3d/variant/deleteOne", { variantID });
export const changeVariantRelease = (variantID, release) => postJson("pe3d/variant/changeRelease", { variantID, release });

// ── Folders ─────────────────────────────────────────────────────────────────
export const createFolder = (baseID, name, parentID) => postJson("pe3d/tree/createFolder", { baseID, name, parentID });
export const renameFolder = (baseID, folderID, name) => postJson("pe3d/tree/renameFolder", { baseID, folderID, name });
export const deleteFolder = (baseID, folderID) => postJson("pe3d/tree/deleteFolder", { baseID, folderID });
export const moveNode = (baseID, nodeID, parentID) => postJson("pe3d/tree/move", { baseID, nodeID, parentID });

// ── Jobs ────────────────────────────────────────────────────────────────────
const jobOptions = { statusPath: PE3D_JOB_STATUS_PATH };

/** Networks → creatives → zip on S3. `variantIDs` exports a folder. */
export const exportVariants = (payload, callbacks) => runJob("pe3d/variant/export", payload, callbacks, jobOptions);

/** What the variant would export to, in bytes; null when the release cannot be read. */
export const variantSizeEstimate = (variantID) => postJson("pe3d/variant/sizeEstimate", { variantID: String(variantID) });

/** The preview network's creative on demo.playin.com.tr. */
export const previewVariantLink = (payload, callbacks) => runJob("pe3d/variant/preview", payload, callbacks, jobOptions);

/** The naming template an export of this base follows unless it brings its own: { exportName, isCompanyOwn, tokens }. */
export const fetchExportNaming = (baseID) => postJson("pe3d/variant/exportNaming", { baseID });

/** One variant per game variant the release ships (`names`), into one folder. */
export const importGameVariants = (payload, callbacks) => runJob("pe3d/variant/importGameVariants", payload, callbacks, jobOptions);

/** The company's design of the variant's release on its base (or `release`, to copy from): { design, contentHash, gameDesign, releases }. */
export const getDesign = (variantID, release) => postJson("pe3d/design/get", { variantID, ...(release && { release }) });

/** Save that design; `contentHash` is the one read, null for the first save. 409 carries the current one. */
export const saveDesign = (variantID, design, contentHash) => postJson("pe3d/design/save", { variantID, design, contentHash: contentHash || null });

export const listPe3dJobs = (baseID) => postJson("pe3d/listJobs", { baseID });
export const followPe3dJob = (job, callbacks) => followJob({ ...job, statusPath: PE3D_JOB_STATUS_PATH }, callbacks);
